/**
 * Program Access — a checkpoint program (a job) checked ahead of a run, and blessed. Each need its
 * current revision declares is shown against the job's grants (its own and its worker's) and against
 * what the job's worker can do, as the mesh decides it: endpoints by the auth policies, table writes
 * and statements by the SQL firewall. Bless grants the job every need, and is refused while the worker
 * cannot do one. FEATURE-20261026-program-access-check-blessing-and-more-grants.
 *
 * Imperative trigger: openProgramAccessDialog({ jobUid, jobName }) from anywhere. The Subject emits to
 * the always-mounted ProgramAccessDialogHost.
 */
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getCheckpointEngineRpcClient, type ProgramAccess, type ProgramAccessNeed } from "../../services/checkpoint-engine-rpc-client";
import { showProgramAccessDialog, type ProgramAccessDialogRequest } from "./program-access-signal";

export const ProgramAccessDialogHost: React.FC = () => {
  const [req, setReq] = useState<ProgramAccessDialogRequest | undefined>(undefined);

  useEffect(() => {
    const sub = showProgramAccessDialog.subscribe((next) => setReq(next));
    return () => sub.unsubscribe();
  }, []);

  return req ? <ProgramAccessDialog key={req.jobUid} req={req} onClose={() => showProgramAccessDialog.next(undefined)} /> : null;
};

// What a need is, as a person reads it: the capability a grant names, else what the plan says.
function needLabel(n: ProgramAccessNeed): string {
  if (n.capability) return n.capability;
  const what = [n.kind, n.name, n.detail].filter(Boolean).join(" ");
  return n.dynamic ? `${what} (computed when the program runs)` : what;
}

function workerMark(n: ProgramAccessNeed): string {
  switch (n.worker) {
    case "yes":
      return "✓ can";
    case "no":
      return "✗ cannot";
    default:
      return "—";
  }
}

const ProgramAccessDialog: React.FC<{ req: ProgramAccessDialogRequest; onClose: () => void }> = ({ req, onClose }) => {
  const [access, setAccess] = useState<ProgramAccess | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [blessing, setBlessing] = useState(false);

  // the check, run when the dialog opens and again after a blessing
  const [checks, setChecks] = useState(0);
  useEffect(() => {
    let open = true;
    getCheckpointEngineRpcClient()
      .checkProgramAccess(req.jobUid)
      .then((a) => open && setAccess(a))
      .catch((e: unknown) => open && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      open = false;
    };
  }, [req.jobUid, checks]);

  const bless = async () => {
    setBlessing(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const granted = await getCheckpointEngineRpcClient().blessProgram(req.jobUid);
      setNotice(granted.length ? `Blessed: granted ${granted.join(", ")}` : "Blessed: it already held every grant it needs");
      setChecks((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBlessing(false);
    }
  };

  const toGrant = access?.needs.filter((n) => n.capability && !n.granted).length ?? 0;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Access: {req.jobName}</DialogTitle>
      <DialogContent data-testid="program-access">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
        {!access && !error && <CircularProgress size={24} />}
        {access && !access.ok && <Alert severity="warning">{access.message}</Alert>}
        {access?.ok && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Runs on {access.workerLabel || "no worker"}; {access.enforced ? "enforces grants: a need without one pauses the program" : "does not enforce grants: only opening a secret needs one"}.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
              {access.blessedAt
                ? `Last blessed ${access.blessedAt}${access.blessedRevisionUid === access.revisionUid ? ", this revision" : ", an earlier revision"}.`
                : "Never blessed."}
            </Typography>
            {access.needs.length === 0 ? (
              <Typography variant="body2">The program needs nothing outside itself.</Typography>
            ) : (
              <Table size="small" data-testid="program-access-needs">
                <TableHead>
                  <TableRow>
                    <TableCell>Need</TableCell>
                    <TableCell>Grant</TableCell>
                    <TableCell>Worker</TableCell>
                    <TableCell>Why</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {access.needs.map((n, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{needLabel(n)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{n.capability ? (n.granted ? "✓ granted" : "✗ no grant") : "—"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: n.worker === "no" ? "error.main" : undefined }}>{workerMark(n)}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{n.workerReason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!access.workerCan && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                The worker cannot do every need the mesh decides (✗), so it cannot be blessed until it can.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={!access?.ok || !access.workerCan || blessing} onClick={() => void bless()}>
          {toGrant ? `Bless: grant the job ${toGrant} need${toGrant === 1 ? "" : "s"}` : "Bless"}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
